import {
  addDays,
  addHours,
  addMonths,
  differenceInCalendarDays,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInMonths,
  differenceInSeconds,
  differenceInYears,
  distanceInWords,
  distanceInWordsStrict,
  format,
  isAfter,
  isSameDay,
  startOfDay,
  startOfHour,
  startOfMonth,
  startOfWeek,
  subDays,
  subHours,
  subMonths,
} from "date-fns";
import { identity } from "lodash";
import { compose } from "redux";

import { Interval } from "constants/date";
import { Weekday } from "constants/filters/weekday-filter";

export function dateDifference(earlierDate: Date, laterDate: Date, interval: Interval, roundUp = false): number {
  switch (interval) {
    default:
      return roundUp ? differenceInCalendarDays(laterDate, earlierDate) : differenceInDays(laterDate, earlierDate);
  }
}

export function formatTimeDistance(date1: Date, date2: Date): string {
  const seconds = differenceInSeconds(date2, date1);
  const minutes = differenceInMinutes(date2, date1);
  const hours = differenceInHours(date2, date1);
  const days = differenceInDays(date2, date1);
  const months = differenceInMonths(date2, date1);
  const years = differenceInYears(date2, date1);

  if (years > 0) return `${years}y`;
  if (months > 0) return `${months}m`;
  if (days > 0) return `${days}d`;
  if (hours > 0) return `${hours}h`;
  if (minutes > 0) return `${minutes}m`;
  return `${seconds}s`;
}

export function timeAgo(
  earlierDate: Date,
  laterDate: Date,
  opts?: {
    includeAgo?: boolean;
    dateFormat?: string;
    useStrict?: boolean;
  }
): string {
  const config = {
    includeAgo: true,
    dateFormat: "MMMM D, YYYY",
    useStrict: true,
    ...opts,
  };

  if (earlierDate.getTime() > laterDate.getTime()) {
    return "just now";
  }

  const diffInDays = differenceInCalendarDays(laterDate, earlierDate);
  if (diffInDays === 0) {
    if (!config.useStrict) {
      return distanceInWords(laterDate, earlierDate, {
        addSuffix: config.includeAgo,
      });
    }
    return distanceInWordsStrict(laterDate, earlierDate, {
      addSuffix: config.includeAgo,
    });
  }
  if (diffInDays === 1) {
    return "yesterday";
  }
  return format(earlierDate, config.dateFormat);
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function prettyDateDifference(earlierDate: Date, laterDate: Date) {
  const diff = Math.abs(Math.floor((laterDate.getTime() - earlierDate.getTime()) / 1000));
  const days = Math.floor((diff / 86400) % 86400);
  const hours = Math.floor((diff / 3600) % 3600);
  const minutes = Math.floor((diff / 60) % 60);
  const seconds = diff % 60;

  if (days > 2) {
    return `${days} days`;
  }

  let result = "";
  if (hours) {
    result += `${hours}h `;
  }

  if ((hours && !minutes) || minutes) {
    result += `${minutes}m `;
  }

  result += `${seconds}s`;
  return result;
}

export function normalizeTimestamp(timestamp: number): number {
  if (String(timestamp).length === 13) {
    return Math.round(timestamp / 1000);
  }
  return timestamp;
}

export function duration(totalSeconds: number): string {
  const isNegative = totalSeconds < 0;
  const absoluteSeconds = Math.abs(totalSeconds);
  const daysInSeconds = 86400;
  const hoursInSeconds = 3600;
  const minuteInSeconds = 60;

  const days = Math.floor(absoluteSeconds / daysInSeconds);
  const divisorForHours = absoluteSeconds % daysInSeconds;
  const hours = Math.floor(divisorForHours / hoursInSeconds);
  const divisorForMinutes = divisorForHours % hoursInSeconds;
  const minutes = Math.floor(divisorForMinutes / minuteInSeconds);
  const divisorForSeconds = divisorForMinutes % minuteInSeconds;
  const seconds = Math.ceil(divisorForSeconds);

  let result = "";

  if (days > 0) {
    result = `${days}d ${hours}h`;
  } else if (hours > 0) {
    result = `${hours}h ${minutes}min`;
  } else if (minutes > 0) {
    result = `${minutes}min ${seconds}s`;
  } else {
    result = `${seconds}s`;
  }

  return isNegative ? `-${result}` : result;
}

export const isDateWithinRange = (date: Date, range: { from?: Date; to?: Date }): boolean => {
  const { from, to } = range;
  if (to && !isSameDay(date, to) && isAfter(date, to)) {
    return false;
  }
  if (from && !isSameDay(date, from) && !isAfter(date, from)) {
    return false;
  }
  return true;
};

const weekdays = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

export function getDateByWeekday(day: Weekday): Date {
  return addDays(startOfWeek(new Date()), weekdays.indexOf(day));
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function getTransformFunction(unit: string, sign: string) {
  // hours
  if (["H", "h"].includes(unit)) {
    if (sign === "-") {
      if (unit === "H") {
        return compose(startOfHour, subHours);
      }
      return subHours;
    }
    if (unit === "H") {
      return compose(startOfHour, addHours);
    }
    return addHours;
  }
  // days
  if (["D", "d"].includes(unit)) {
    if (sign === "-") {
      if (unit === "D") {
        return compose(startOfDay, subDays);
      }
      return subDays;
    }
    if (unit === "D") {
      return compose(startOfDay, addDays);
    }
    return addDays;
  }
  // months
  if (["M", "m"].includes(unit)) {
    if (sign === "-") {
      if (unit === "M") {
        return compose(startOfMonth, subMonths);
      }
      return subMonths;
    }
    if (unit === "M") {
      return compose(startOfMonth, addMonths);
    }
    return addMonths;
  }

  return identity;
}

const relativeDateRegex = /^(-?)(\d)([HhDdMm])$/;

/**
 * converts relative date format to YYYY-MM-DD
 * supports only M, m, D, d
 */
export function getSpecificDateFromRelative(relativeDate: string, relativeToDate: Date = new Date()): Date {
  if (!relativeDate) {
    return relativeToDate;
  }
  const match = relativeDateRegex.exec(relativeDate);
  if (!match) {
    // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'Date'.
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, sign, value, unit] = match;

  // eslint-disable-next-line no-nested-ternary
  const transformFunction = getTransformFunction(unit, sign);
  // @ts-expect-error TS2322
  return transformFunction(relativeToDate, Number(value));
}
